import axios from "axios";
import moment from "moment";
import AffiliateTable from "./AffiliateTable";
import React, { Component } from "react";
import { DateRangePicker } from "react-dates";
import { connect } from "react-redux";
import {
  Theme,
  createStyles,
  withStyles,
  WithStyles,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  TablePagination,
  Dialog,
  Tab,
  Tabs,
} from "@material-ui/core";
import { NavigateNext, NavigateBefore, Clear } from "@material-ui/icons";
import { URL } from "../../config/config";
import AddIcon from "@material-ui/icons/AddOutlined";
import AddAffiliate from "./AddAffiliate";

const createInitialState = () => ({
  rowsPerPage: 15,
  hasMore: true,
  skip: 0,
  timeStamp: new Date(),
  search: "",
  partners: [],
  err: null,
  isLoading: false,
  page: 0,
  applied: {
    createdAt: {
      startDate: null,
      endDate: null,
      focus: null,
    },
  },
  openAddPartnerDialog: false,
  tabValue: 0,
  tier0: [],
  tier1: [],
  tier2: [],
  tier0Count: 0,
  tier1Count: 0,
  tier2Count: 0,
});

class AffiliatePage extends Component {
  constructor(props) {
    super(props);
    this.state = createInitialState();
  }

  getVisibleTabs = () => {
    const { permissions } = this.props;
    const affiliatesPermission = permissions.find(
      (p) => p.label === "affiliates"
    );
    if (!affiliatesPermission || !Array.isArray(affiliatesPermission.subroutes))
      return [];
    // Map label to display name and tier
    const labelMap = {
      regular: { display: "Regular (20%)", tier: 0 },
      growth: { display: "Growth (25%)", tier: 1 },
      integration: { display: "Integration (30%)", tier: 2 },
    };
    return affiliatesPermission.subroutes
      .filter((sr) => sr.read)
      .map((sr) => ({
        label: sr.label,
        display: labelMap[sr.label]?.display || sr.label,
        tier: labelMap[sr.label]?.tier,
        write: sr.write,
        actions: sr.actions,
      }));
  };

  // Helper to get the actual tab type (label) and tier for the current tabValue
  getCurrentTab = () => {
    const visibleTabs = this.getVisibleTabs();
    return visibleTabs[this.state.tabValue];
  };

  hasWriteAccess = () => {
    const visibleTabs = this.getVisibleTabs();
    return visibleTabs[this.state.tabValue]?.write;
  };

  componentDidMount() {
    // Ensure tabValue is valid (in case permissions change)
    const visibleTabs = this.getVisibleTabs();
    if (visibleTabs.length === 0) return; // No tabs to show
    if (this.state.tabValue >= visibleTabs.length) {
      this.setState({ tabValue: 0 }, this.fetchAfffiliates);
    } else {
      this.fetchAfffiliates();
    }
  }

  closeAddPartnerDialog = () => this.setState({ openAddPartnerDialog: false });

  onAddedPartnerDialog = () => {
    this.closeAddPartnerDialog();
    this.setState(createInitialState(), () => {
      this.fetchAfffiliates();
    });
  };

  openAddPartnerDialog = () => this.setState({ openAddPartnerDialog: true });

  getPageRows = () => {
    const { page, rowsPerPage, tier0, tier1, tier2 } = this.state;
    const currentTier = this.getCurrentTab()?.tier;
    const dataForTier =
      currentTier === 0
        ? tier0
        : currentTier === 1
        ? tier1
        : currentTier === 2
        ? tier2
        : [];
    return dataForTier.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  };

  handleChangePage = (event, newPage) => {
    const { page, rowsPerPage, tier0, tier1, tier2 } = this.state;
    const currentTier = this.getCurrentTab()?.tier;
    const currentPartners =
      currentTier === 0
        ? tier0
        : currentTier === 1
        ? tier1
        : currentTier === 2
        ? tier2
        : [];
    const step = newPage - page;
    if (step === 1) {
      const pageRows = currentPartners.slice(
        newPage * rowsPerPage,
        newPage * rowsPerPage + rowsPerPage
      );
      if (!pageRows.length) {
        this.fetchAfffiliates();
      }
    }
    this.setState({ page: newPage });
  };

  handleInput = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value }, () => {
      // If search is cleared, reload data for current tier
      if (name === "search" && value.trim() === "") {
        this.setState(
          {
            timeStamp: new Date(),
            skip: 0,
            page: 0,
            hasMore: true,
            partners: [],
            isLoading: true,
            err: null,
          },
          () => {
            this.fetchAfffiliates();
          }
        );
      }
    });
  };

  handleEnter = (e) => {
    if (e.key === "Enter") {
      this.setState(
        {
          timeStamp: new Date(),
          skip: 0,
          page: 0,
          hasMore: true,
          partners: [],
          isLoading: true,
          err: null,
        },
        () => {
          this.fetchAfffiliates();
        }
      );
    }
  };

  clearDate = (filterKey) => {
    const applied = { ...this.state.applied };
    applied[filterKey].startDate = null;
    applied[filterKey].endDate = null;
    this.setState({ applied }, () => {
      // Reload data for current tier when date filter is cleared
      this.setState(
        {
          timeStamp: new Date(),
          skip: 0,
          page: 0,
          hasMore: true,
          partners: [],
          isLoading: true,
          err: null,
        },
        () => {
          this.fetchAfffiliates();
        }
      );
    });
  };

  checkDateInput = (filterKey) => {
    const applied = { ...this.state.applied };
    const s = applied[filterKey];
    if (s.startDate && s.endDate) {
      return;
    } else {
      this.clearDate(filterKey);
    }
  };

  setFocus = (filterKey, focusedInput) => {
    const applied = { ...this.state.applied };
    applied[filterKey].focusedInput = focusedInput;
    this.setState({ applied });
  };

  setDate = (filterKey, startDate, endDate) => {
    const applied = { ...this.state.applied };

    // Ensure start of day and end of day
    if (startDate) {
      startDate = moment(startDate).startOf("day");
    }
    if (endDate) {
      endDate = moment(endDate).endOf("day");
    }

    applied[filterKey].startDate = startDate;
    applied[filterKey].endDate = endDate;
    this.setState({ applied });
  };

  applyFilter = () => {
    this.setState(
      {
        timeStamp: new Date(),
        skip: 0,
        page: 0,
        hasMore: true,
        partners: [],
        isLoading: true,
        err: null,
      },
      () => {
        this.fetchAfffiliates();
      }
    );
  };

  handleTabs = (event, newValue) => {
    this.setState(
      {
        timeStamp: new Date(),
        skip: 0,
        page: 0,
        hasMore: true,
        partners: [],
        isLoading: true,
        err: null,
        tabValue: newValue,
      },
      () => {
        this.fetchAfffiliates(true);
      }
    );
  };

  fetchAfffiliates = async (fromTabChange = false) => {
    this.setState({ isLoading: true, err: null });
    try {
      const t = this.state;
      let filter = {};
      if (t.search) {
        filter["search"] = t.search;
      }
      if (t.applied.createdAt.startDate) {
        filter["createdAt"] = {
          $gte: t.applied.createdAt.startDate,
          $lte: t.applied.createdAt.endDate,
        };
      }
      const currentTier = this.getCurrentTab()?.tier;
      const affiliatePlan =
        currentTier === 0
          ? "tier0"
          : currentTier === 1
          ? "tier1"
          : currentTier === 2
          ? "tier2"
          : undefined;
      if (
        t.search ||
        t.applied.createdAt.startDate ||
        t.applied.createdAt.endDate
      ) {
        filter.affiliatePlan = { $in: ["tier0", "tier1", "tier2"] };
      } else if (affiliatePlan) filter.affiliatePlan = affiliatePlan;
      const queryObj = {
        skip: t.skip,
        timeStamp: t.timeStamp,
        rowsPerPage: t.rowsPerPage,
        search: "",
        allFilters: JSON.stringify({ ...filter }),
      };

      const response = await axios.get(`${URL}/superadmin/get-affiliates`, {
        params: queryObj,
      });
      const { newSkip, affiliates } = response.data;
      const hasMore = affiliates.length >= this.state.rowsPerPage;

      this.setState({
        skip: newSkip,
        partners: [...this.state.partners, ...affiliates],
        hasMore,
        isLoading: false,
      });

      const tier0 = this.state.partners.filter(
        (affiliate) => affiliate.affiliatePlan === "tier0"
      );
      const tier1 = this.state.partners.filter(
        (affiliate) => affiliate.affiliatePlan === "tier1"
      );
      const tier2 = this.state.partners.filter(
        (affiliate) => affiliate.affiliatePlan === "tier2"
      );

      // Auto tab switch logic - respect visible tabs order and map to their tiers
      if (!fromTabChange) {
        const visibleTabs = this.getVisibleTabs();
        const currentTierForTab = this.getCurrentTab()?.tier;
        const currentTabData =
          currentTierForTab === 0
            ? tier0
            : currentTierForTab === 1
            ? tier1
            : currentTierForTab === 2
            ? tier2
            : [];

        if (currentTabData.length === 0 && visibleTabs.length > 0) {
          const tierDataMap = { 0: tier0, 1: tier1, 2: tier2 };
          const firstTabWithDataIdx = visibleTabs.findIndex(
            (t) => (tierDataMap[t.tier] || []).length > 0
          );
          if (firstTabWithDataIdx !== -1) {
            this.setState({ tabValue: firstTabWithDataIdx });
          }
        }
      }

      this.setState({
        tier0Count: tier0.length,
        tier0: tier0,
        tier1Count: tier1.length,
        tier1: tier1,
        tier2Count: tier2.length,
        tier2: tier2,
      });
    } catch (err) {
      this.setState({ err, isLoading: false });
    }
  };

  /** @param {any} affiliate */
  setPartner = (affiliate) => {
    const partners = [...this.state.partners];
    const foundIndex = partners.findIndex((x) => x._id == affiliate._id);
    partners[foundIndex] = affiliate;
    const tier0 = partners.filter(
      (affiliate) => affiliate.affiliatePlan === "tier0"
    );
    const tier1 = partners.filter(
      (affiliate) => affiliate.affiliatePlan === "tier1"
    );
    const tier2 = partners.filter(
      (affiliate) => affiliate.affiliatePlan === "tier2"
    );
    this.setState({
      tier0Count: tier0.length,
      tier0: tier0,
      tier1Count: tier1.length,
      tier1: tier1,
      tier2Count: tier2.length,
      tier2: tier2,
      partners: partners,
    });
  };

  getAffiliateAggregatedReport = () => {
    this.setState({ successAffiliateDataLoading: false });
    axios
      .post(
        `${URL}/superadmin/reporting/affiliate/consolidated-commission-report`,
        { query: "all" }
      )
      .then((res) => {
        const cbLogs = res.data?.data;
        if (!cbLogs || !cbLogs.length) {
          console.error("No data available to generate CSV.");
          return "failure";
        }
        // Function to flatten JSON
        const flattenObject = (obj, parent = "", res = {}) => {
          for (let key in obj) {
            let propName = parent ? `${parent}/${key}` : key;
            if (typeof obj[key] === "object" && obj[key] !== null) {
              flattenObject(obj[key], propName, res);
            } else {
              res[propName] = obj[key];
            }
          }
          return res;
        };
        // Flatten all rows
        const flattenedData = cbLogs.map((row) => flattenObject(row));
        // Get all headers from the flattened data
        const headers = Array.from(
          flattenedData.reduce((acc, row) => {
            Object.keys(row).forEach((key) => acc.add(key));
            return acc;
          }, new Set())
        );
        // Create CSV content
        const replacer = (key, value) => (value === null ? "" : value);
        const csv = [
          headers.join(","), // CSV header row
          ...flattenedData.map((row) =>
            headers
              .map((fieldName) => JSON.stringify(row[fieldName], replacer))
              .join(",")
          ),
        ].join("\r\n");
        // Download the file as CSV
        var downloadLink = document.createElement("a");
        var blob = new Blob(["\ufeff", csv]);
        var url = window.URL.createObjectURL(blob);
        downloadLink.href = url;
        downloadLink.download = "AFFILIATE_STATS_ALL" + ".csv";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        this.setState({
          successAffiliateData: true,
          successAffiliateDataLoading: false,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setState({
          status: "error",
          statusMessage: "Unable to fetch Data",
          loading: false,
          successAffiliateDataConsolidated: true,
          successAffiliateData: true,
        });
      });
  };

  render() {
    const { classes, agent, permissions } = this.props;
    const { tabValue, rowsPerPage } = this.state;
    const visibleTabs = this.getVisibleTabs();
    console.log("visible tabs", visibleTabs);
    // if (visibleTabs.length === 0) {
    //   return <div>No access to any affiliate tiers.</div>;
    // }
    const affiliatesPermission = permissions.find(
      (p) => p.label === "affiliates"
    );
    const addAffiliatePermission =
      affiliatesPermission?.actions?.includes("addAffiliate");

    const reportPermission =
      affiliatesPermission?.actions?.includes("report") ||
      addAffiliatePermission;

    return (
      <div className={classes.root}>
        <Grid
          container
          className={`${classes.pageTitleContainer} ${classes.fullWidth}`}
          spacing={2}
        >
          <Grid item>
            <Typography variant="h3">Affiliates</Typography>
          </Grid>
          <Grid item>
            <TextField
              name="search"
              placeholder="Search Affiliate"
              className={classes.textField}
              onChange={this.handleInput}
              onKeyPress={this.handleEnter}
            />
          </Grid>
          <Grid item>
            <DateFilter
              filterKey="createdAt"
              filterName="Created At"
              classes={classes}
              applied={this.state.applied}
              setDate={this.setDate}
              setFocus={this.setFocus}
              checkDateInput={this.checkDateInput}
              clearDate={this.clearDate}
            />
          </Grid>
          <Grid item>
            <Button
              color="primary"
              variant="contained"
              onClick={this.applyFilter}
            >
              Apply
            </Button>
          </Grid>
          <Grid item xs></Grid>
          <Grid item>
            {!reportPermission ? (
              <></>
            ) : (
              <Button
                color="primary"
                variant="contained"
                onClick={this.getAffiliateAggregatedReport}
                disabled={this.successAffiliateDataLoading}
              >
                Report
              </Button>
            )}
          </Grid>
          <Grid item>
            {!addAffiliatePermission ? (
              <></>
            ) : (
              <Button
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={this.openAddPartnerDialog}
              >
                Add Affiliate
              </Button>
            )}
          </Grid>
        </Grid>
        <Box my={3} />
        <Grid container justify="center">
          <Grid item xs={12} md={11} lg={10}>
            {visibleTabs.length === 0 ? (
              <div>No access to any affiliate tiers.</div>
            ) : (
              <div className={classes.tableContainer}>
                <Tabs
                  value={tabValue}
                  onChange={this.handleTabs}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  {visibleTabs.map((tab, idx) => (
                    <Tab key={tab.label} label={`${tab.display}`} />
                  ))}
                </Tabs>
                <AffiliateTable
                  partners={this.getPageRows()}
                  isLoading={this.state.isLoading}
                  setPartner={this.setPartner}
                  hasWriteAccess={this.hasWriteAccess()}
                  actions={this.getCurrentTab()?.actions}
                />
              </div>
            )}
          </Grid>
        </Grid>
        <div className={classes.fixedBottomContainer}>
          {(() => {
            const currentTier = this.getCurrentTab()?.tier;
            const currentLength =
              currentTier === 0
                ? this.state.tier0.length
                : currentTier === 1
                ? this.state.tier1.length
                : currentTier === 2
                ? this.state.tier2.length
                : 0;
            return (
              <TablePagination
                component="div"
                count={
                  currentLength +
                  (this.state.hasMore ? this.state.rowsPerPage : 0)
                }
                page={this.state.page}
                onPageChange={this.handleChangePage}
                rowsPerPage={this.state.rowsPerPage}
                rowsPerPageOptions={[25]}
              />
            );
          })()}
        </div>

        <Dialog open={this.state.openAddPartnerDialog}>
          <AddAffiliate
            onClose={this.closeAddPartnerDialog}
            onSuccess={this.onAddedPartnerDialog}
          />
        </Dialog>
      </div>
    );
  }
}

function NavigationWrapper(props) {
  const { classes } = props;
  return (
    <div className={classes.navigationWrapper}>
      <IconButton size="small">{props.children}</IconButton>
    </div>
  );
}

function DateFilter({
  filterKey,
  applied,
  setFocus,
  setDate,
  checkDateInput,
  clearDate,
  ...props
}) {
  const { classes, filterName } = props;
  const A = applied[filterKey];
  return (
    <Box className={classes.filterTypeContainer}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        {A.startDate && A.endDate ? (
          <Grid item>
            <IconButton
              color="primary"
              size="small"
              variant="filled"
              onClick={() => clearDate(filterKey)}
            >
              <Clear />
            </IconButton>
          </Grid>
        ) : (
          ""
        )}
        <Grid item>
          <Typography variant="body1">{filterName}</Typography>
        </Grid>
        <Grid item xs>
          <DateRangePicker
            startDatePlaceholderText="From"
            endDatePlaceholderText="To"
            onClose={() => checkDateInput(filterKey)}
            startDateId="startDate"
            endDateId="endDate"
            startDate={A.startDate}
            endDate={A.endDate}
            onDatesChange={({ startDate, endDate }) =>
              setDate(filterKey, startDate, endDate)
            }
            focusedInput={A.focusedInput}
            onFocusChange={(focusedInput) => setFocus(filterKey, focusedInput)}
            navPosition="navPositionTop"
            numberOfMonths={1}
            navPrev={
              <NavigationWrapper classes={classes}>
                <NavigateBefore />
              </NavigationWrapper>
            }
            navNext={
              <NavigationWrapper classes={classes}>
                <NavigateNext />
              </NavigationWrapper>
            }
            hideKeyboardShortcutsPanel
            customArrowIcon={null}
            screenReaderInputMessage={" "}
            small
            readOnly
            isOutsideRange={(day) => moment().diff(day) < 0}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

const styles = (theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
      background: "rgb(249,249,249)",
      boxSizing: "border-box",
      overflowX: "hidden",
      position: "relative",
    },
    pageTitleContainer: {
      position: "sticky",
      zIndex: 100,
      top: 0,
      height: 60,
      boxSizing: "border-box",
      alignItems: "center",
      padding: "0 60px",
      [theme.breakpoints.down("md")]: {},
      [theme.breakpoints.down("sm")]: {},
    },
    fullWidth: {
      background: "white",
    },
    container: {
      background: "white",
      borderRadius: "8px",
    },
    fixedBottomContainer: {
      position: "fixed",
      height: "60px",
      borderTop: "1px solid lightgrey",
      background: "white",
      bottom: "0",
      left: "71px",
      right: "0",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("sm")]: {
        left: "0px",
        height: "50px",
      },
    },
    tableContainer: {
      top: "60px",
      height: "calc(100vh - 70px - 60px - 40px)",
      width: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      position: "sticky",
      [theme.breakpoints.down("sm")]: {
        height: "calc(100vh - 70px - 50px - 10px - 50px)",
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        boxSizing: "border-box",
      },
    },
    textField: {
      width: 300,
    },
  });

const connectedPartnersPage = connect((state) => ({
  agent: state.login.user,
  permissions: state.login.permissions,
}))(AffiliatePage);

export default withStyles(styles)(connectedPartnersPage);
